/* eslint-disable max-lines */
import {
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  TabHeadersConfig,
  TabHeadersContent
} from '../../../models/ui-tabs.interfaces';
import { BaseProperties } from '../../../models/ui.interfaces';
import { TabComponent } from '../tab/tab.component';

/** The `TabHeadersComponent` component contains the `TabComponent` items and controls scroll functionality if content has overflow. */
@Component({
  selector: 'idp-tab-headers',
  templateUrl: './tab-headers.component.html',
  styleUrls: ['./tab-headers.component.scss']
})
export class TabHeadersComponent
  implements
    OnChanges,
    OnDestroy,
    BaseProperties<TabHeadersConfig, TabHeadersContent>
{
  @ViewChild('tabList') headerContainer = {} as ElementRef;
  @ViewChildren('tabItem') allTabHeaders: QueryList<ElementRef> =
    {} as QueryList<ElementRef>;

  @ContentChildren(TabComponent) tabQuery: QueryList<TabComponent> =
    {} as QueryList<TabComponent>;

  @Input() config: TabHeadersConfig = {} as TabHeadersConfig;
  @Input() content: TabHeadersContent = {} as TabHeadersContent;

  currentSelectedTab = 0;
  @Output() selectedTab: EventEmitter<number> = new EventEmitter<number>();
  changesSubscription: Subscription = {} as Subscription;

  constructor(protected elementRef: ElementRef) {}

  /* Controls the keydown events used for tabbing through the headings - keyboard accessibility */
  @HostListener('keydown', ['$event'])
  keyboardInput(eventkey: Event) {
    const event: KeyboardEvent = eventkey as KeyboardEvent;
    const tabsArray = this.content.tabs.toArray();
    this.setArrowLeftorRight(event, tabsArray);
    switch (event.key) {
      case 'Home':
        this.setHeaderFocus(event, tabsArray, 0);
        break;
      case 'End':
        this.setHeaderFocus(event, tabsArray, this.allTabHeaders.length - 1);
        break;
      case ' ':
        this.selectTab(
          event.target as HTMLElement,
          tabsArray[this.currentSelectedTab],
          this.currentSelectedTab
        );
        break;
      default:
        break;
    }
  }

  ngOnChanges(): void {
    this.onChangeValue();
  }

  /** set/activate first tab whenever content refreshes */
  onChangeValue() {
    this.content.tabs = !this.content.tabs ? this.tabQuery : this.content.tabs;
    this.changesSubscription = this.content.tabs.changes.subscribe(() => {
      this.setFirstTab();
    });
    this.setFirstTab();
  }

  /** handles arrow left event  */
  setArrowLeft(event: KeyboardEvent, tabsArray: TabComponent[]) {
    if (event.key === 'Left' || event.key === 'ArrowLeft') {
      this.currentSelectedTab > 0
        ? this.setHeaderFocus(event, tabsArray, this.currentSelectedTab - 1)
        : this.setHeaderFocus(event, tabsArray, this.allTabHeaders.length - 1);
    }
  }

  /** handles arrow right event */
  setArrowRight(event: KeyboardEvent, tabsArray: TabComponent[]) {
    this.currentSelectedTab < this.allTabHeaders.length - 1
      ? this.setHeaderFocus(event, tabsArray, this.currentSelectedTab + 1)
      : this.setHeaderFocus(event, tabsArray, 0);
  }

  /**  listen to host and event out arrow left & right */
  setArrowLeftorRight(event: KeyboardEvent, tabsArray: TabComponent[]) {
    if (event.key === 'Right' || event.key === 'ArrowRight') {
      this.setArrowRight(event, tabsArray);
    } else {
      this.setArrowLeft(event, tabsArray);
    }
  }

  /** controls tab focus functionality */
  onTabFocus(ref: HTMLElement, index: number) {
    this.currentSelectedTab = index;
    this.headerContainer.nativeElement.parentElement.scrollLeft = 0;
  }

  /** selecting tab sets it active and deactivates othetr tabs */
  selectTab(ref: HTMLElement, tab: TabComponent, tabIndex: number) {
    // Condition to check if the tab is disabled. => isDisable true, Don't do anything, else Do the following logic
    if (!tab.content.isDisabled) {
      this.currentSelectedTab = tabIndex;
      this.content.tabs.forEach((_tab) => (_tab.active = false));
      tab.active = true;
      tab.doSelect();
      this.selectedTab.emit(this.currentSelectedTab);
    }
  }

  /** handles tab header focus function */
  setHeaderFocus(
    event: KeyboardEvent,
    tabsArray: TabComponent[],
    elementIndex: number
  ) {
    event.preventDefault();
    if (this.config.followFocus) {
      this.selectTab(event.target as HTMLElement, tabsArray[elementIndex], 0);
    }
    this.allTabHeaders.toArray()[elementIndex].nativeElement.focus();
  }

  ngOnDestroy() {
    if (this.changesSubscription) {
      this.changesSubscription.unsubscribe();
    }
  }

  /** set first tab and deactivate others */
  protected setFirstTab() {
    setTimeout(() => {
      let firstTab = this.content.tabs.find((tab) => tab.active);
      if (!firstTab && this.content.tabs.first) {
        firstTab = this.content.tabs.first;
        firstTab.active = true;
      }
      if (firstTab) {
        firstTab.doSelect();
      }
    });
  }
}
